<template>
    <b-container fluid>
        <b-row>
            <b-col md="8"><h2>{{ title | capitalize }}</h2></b-col>
            <b-col md="4">
                <b-button class="float-right" @click="exportMembers()">
                    <font-awesome-icon icon="file-csv"></font-awesome-icon>
                    Export All
                </b-button>
            </b-col>
            <b-col md="6" class="my-1">
                <b-form-group label-cols-sm="3" label="Filter" class="mb-0">
                    <b-input-group>
                        <b-form-input v-model="filter" placeholder="Type to Search"></b-form-input>
                        <b-input-group-append>
                            <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-col>

            <b-col md="6" class="my-1">
                <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
                    <b-form-select v-model="perPage" :options="pageOptions"></b-form-select>
                </b-form-group>
            </b-col>
        </b-row>
        <b-table striped hover :items="filterOutTerms" :fields="fields"
                 :per-page="perPage" :current-page="currentPage"
                 :filter="filter"
                 @filtered="onFiltered">
            <template slot="tools" slot-scope="data">
                <b-button variant="success" :to="'/dashboard/member/' + data.item.cert" v-if="!displayPending">View</b-button>
                <b-button variant="success" :to="'/dashboard/producer/pending-details/' + data.item.id" v-if="displayPending">View</b-button>
                <b-dropdown variant="primary" right text="Actions">
                    <b-dropdown-item @click="showAuthUser(data)" v-if="requestAuth">Request Claims Access
                    </b-dropdown-item>
                    <b-dropdown-item @click="reinstateUser(data)" v-if="displayTerms">Reinstate</b-dropdown-item>
                    <b-dropdown-item @click="cancelRequest(data.item.id, data.index)" v-if="displayPending">Cancel
                        Request
                    </b-dropdown-item>
                    <b-dropdown-item @click="requestIDCard(data.item.cert, data.index)" v-if="!displayPending">Download ID Card</b-dropdown-item>
                    <b-dropdown-item @click="generateCert(data.item.cert, data.item.plans, data.index)" v-if="!displayPending">Download Certificate</b-dropdown-item>
                    <b-dropdown-item :to="'/dashboard/member/' + data.item.cert + '/address'" v-if="!displayPending">Change Address/Contact
                        Info
                    </b-dropdown-item>
                    <b-dropdown-item :to="'/dashboard/member/' + data.item.cert + '/dependents'" v-if="!displayPending">Add/Change Dependents
                    </b-dropdown-item>
                    <b-dropdown-item variant="danger" v-if="!displayTerms && !displayPending" v-b-modal="'term_model'"
                                     @click="selected(data.item)">
                        Terminate Enrollee
                    </b-dropdown-item>
                </b-dropdown>

            </template>
            <template slot="quickView" slot-scope="data">
                <font-awesome-icon icon="search" @click="data.toggleDetails"></font-awesome-icon>
            </template>
            <template slot="row-details" slot-scope="data">
                <b-card>
                    <member-card :member="data.item"></member-card>
                </b-card>
            </template>
        </b-table>
        <b-row>
            <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
            ></b-pagination>
        </b-row>
        <b-modal
            ref="modal"
            :title="selectedUser.firstName + ' ' + selectedUser.lastName"
            id="term_model"
            ok-title="Terminate"
            ok-variant="danger"
            @ok="terminate"
        >
            <b-form ref="form" inline>
                <p style="font-weight: bold">
                    Please confirm that you want to terminate all coverage for ({{ selectedUser.cert }}) and dependents?
                </p>
                <label :state="termDateState" class="mr-sm-2" for="termdate">Termination Effective Date:</label>
                <b-input :state="termDateState" v-model="termDate" id="termdate" type="date" md="3" required></b-input>
                <b-form-text>Coverage will be effective until the end of the termination date.</b-form-text>
            </b-form>
            <b-alert show variant="danger" v-if="termError">
                An error occurred updating the member. Please try again or contact us.
            </b-alert>
        </b-modal>
    </b-container>
</template>

<script>
    import MemberCard from './MemberCard';
    import axios from 'axios';
    import config from '@/config/config.js';

    export default {
        name: 'MemberList',
        components: {MemberCard},
        props: {
            items: Array,
            title: String,
            displayTerms: {
                default: false
            },
            displayPending: {
                default: false
            },
            groupcode: null,
            fields: {
                default: function() {
                    return [
                        {
                            key: 'quickView',
                            sortable: false,
                            label: ''
                        },
                        {
                            key: 'group',
                            sortable: true
                        },
                        {
                            key: 'firstName',
                            sortable: true
                        },
                        {
                            key: 'lastName',
                            sortable: true
                        },
                        {
                            key: 'cert',
                            sortable: false,
                            'label': 'Member ID'
                        },
                        {
                            key: 'city',
                            sortable: true
                        },
                        {
                            key: 'department',
                            sortable: true,
                            'label': 'Dept'
                        },
                        {
                            key: 'tools',
                            sortable: false
                        }
                    ];
                }
            }
        },
        data() {
            return {
                perPage: 15,
                currentPage: 1,
                filter: null,
                filteredItems: [],
                pageOptions: [15, 35, 60, 100],
                selectedUser: {},
                termDateState: null,
                termDate: null,
                termError: null,
                termBox: '',
                hipaaBox: '',
                totalRows: 0,
                requestAuth: config.applicationSettings.requestHipaa
            };
        },
        methods: {
            exportMembers() {
                axios.get(config.apiUrl() + 'producer/getGroup/' + this.groupcode + '/membershipExport', {headers: {'Content-Type': 'arraybuffer'}}).then(response => {
                    let blob = new Blob([response.data], {type: 'octet-stream'});
                    let link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = this.groupcode + '_membership_export.csv';
                    link.click();
                });
            },
            selected(item) {
                this.selectedUser = item;
            },
            terminate(bvModalEvt) {
                bvModalEvt.preventDefault();
                const valid = this.$refs.form.checkValidity();
                this.termDateState = valid ? 'valid' : 'invalid';
                if (valid && this.termDate) {
                    let payload = {
                        cert: this.selectedUser.cert,
                        effectiveDate: this.termDate,
                        originalRecord: this.selectedUser,
                        groupCode: this.groupcode
                    };
                    axios.post(config.apiUrl() + 'member/term/' + this.selectedUser.cert, payload).then(response => {
                        if (response.data.msg === 'ok') {
                            let tempTermed = localStorage.getItem('tempTermed');
                            tempTermed = tempTermed ? tempTermed.split(',') : [];
                            tempTermed.push(this.selectedUser.cert);
                            localStorage.setItem('tempTermed', tempTermed.toString());
                            this.$nextTick(() => {
                                this.$refs.modal.hide();
                            });
                        } else {
                            this.termError = response.data.msg;
                        }
                    }, err => {
                        this.termError = err;
                    });
                }
                //not valid
                return;
            },
            onFiltered(filteredItems) {
                // Trigger pagination to update the number of buttons/pages due to filtering
                this.totalRows = filteredItems.length;
                this.currentPage = 1;
            },
            showAuthUser(data) {
                this.hipaaBox = '';
                this.$bvModal.msgBoxConfirm(
                    'You are requesting claims data for this member, do you wish to continue?', {
                        title: 'HIPAA Authorization for ' + data.item.firstName + ' ' + data.item.lastName,
                        size: 'md',
                        buttonSize: 'sm',
                        okVariant: 'primary',
                        okTitle: 'YES',
                        cancelTitle: 'NO',
                        footerClass: 'p-2',
                        hideHeaderClose: false,
                        centered: true
                    })
                    .then(value => {

                    })
                    .catch(err => {
                        // An error occurred
                    });
            },
            reinstateUser(data) {
                let payload = {
                    cert: data.item.cert,
                    groupCode: data.item.underwriter + data.item.group
                };
                axios.post(config.apiUrl() + 'member/reinstate/' + data.item.cert, payload).then(response => {
                    if (response.data.msg === 'ok') {
                        let tempTermed = localStorage.getItem('tempTermed');
                        tempTermed = tempTermed ? tempTermed.split(',') : [];
                        this.items.splice(data.index, 1);
                        tempTermed.splice(data.index, 1);
                        localStorage.setItem('tempTermed', tempTermed.toString());
                    } else {
                        this.termError = response.data.msg;
                    }
                }, err => {
                    this.termError = err;
                });
            },
            cancelRequest(id, index) {
                if (window.confirm('Are you sure you wish to cancel this request?')) {
                    axios.delete(config.apiUrl() + 'member/cancel-request/' + id).then(response => {
                        if (response.data.msg === 'ok') {
                            let tempTermed = localStorage.getItem('tempTermed');
                            tempTermed = tempTermed ? tempTermed.split(',') : [];
                            this.items.splice(index, 1);
                            tempTermed.splice(index, 1);
                            localStorage.setItem('tempTermed', tempTermed.toString());
                        } else {
                            this.termError = response.data.msg;
                        }
                    }, err => {
                        this.termError = err;
                    });
                }
            },
            requestIDCard(cert, index) {
                axios.get(config.apiUrl() + 'member/id-card/' + cert).then(response => {
                    window.open(response.data, '_blank');
                }, err => {
                    this.$router.push('/dashboard/member/' + cert + '/temp-id-card');
                });
            },
            generateCert(cert, plans, index) {
                if (Array.isArray(plans)) {
                    let found = false;
                    for (let i = 0; i < plans.length; i++) {
                        const plan = plans[i];
                        console.log(plan, plan.product === 'DE', plan.template_id);
                        if (plan.product === 'DE' && plan.template_id) {
                            found = true;
                            axios.post(config.apiUrl() + 'member/certificate', {cert, group: this.groupcode, plan_id: plan.plan_id, template_id: plan.template_id, template: 'cert'}).then(response => {
                                this.$router.push('/dashboard/document/cert/' + response.data.hash);
                            });
                        }
                    }
                    if (!found) window.alert('Certificate could not be generated for this member. Please contact us for further assistance. (1)');
                } else {
                    window.alert('Certificate could not be generated for this member. Please contact us for further assistance. (2)');
                }
                // this.$router.push('/dashboard/member/' + cert + '/certificate');
            }
        },
        computed: {
            filterOutTerms() {
                let tempTermed = localStorage.getItem('tempTermed');
                if (tempTermed && this.displayTerms === false) {
                    let members = [];
                    tempTermed = tempTermed ? tempTermed.split(',') : [];
                    this.items.forEach(member => {
                        if (!tempTermed.includes(member.cert)) {
                            members.push(member);
                        }
                    });
                    return members;
                } else {
                    return this.items;
                }
            }
        }
        ,
        mounted() {
            // Set the initial number of items
            this.totalRows = this.items.length;
        }
    };
</script>

<style scoped>

</style>
